// A tiny IndexedDB key-value store for the guardian app (holder key, cached passes). IndexedDB
// keeps a non-extractable CryptoKey by structured clone, which no string store can.
// Rejected: Dexie (the kiosk's store) for this: about 30 KB gzip in the guardian bundle for a
// single get/set table.

export interface Kv {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
}

const STORE = "kv";

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });
}

export function openKv(name: string, idb: IDBFactory = indexedDB): Kv {
  let db: Promise<IDBDatabase> | null = null;
  const open = () => (db ??= new Promise((resolve, reject) => {
    const r = idb.open(name, 1);
    r.onupgradeneeded = () => { r.result.createObjectStore(STORE); };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => { db = null; reject(r.error); };
  }));
  const tx = async (mode: IDBTransactionMode) => (await open()).transaction(STORE, mode).objectStore(STORE);
  return {
    get: async <T>(key: string) => (await req((await tx("readonly")).get(key))) as T | undefined,
    set: async (key, value) => { await req((await tx("readwrite")).put(value, key)); },
    delete: async (key) => { await req((await tx("readwrite")).delete(key)); },
    clear: async () => { await req((await tx("readwrite")).clear()); },
  };
}

export function memoryKv(): Kv {
  const m = new Map<string, unknown>();
  return {
    get: async <T>(k: string) => m.get(k) as T | undefined,
    set: async (k, v) => { m.set(k, v); },
    delete: async (k) => { m.delete(k); },
    clear: async () => { m.clear(); },
  };
}
